import React, { useState, useEffect } from 'react';
// // // import { useTranslation } from 'react-i18next';   // Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  Save,
  RefreshCw,
  Settings,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Target,
  Activity,
  Globe,
  Calculator,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Star,
  Award,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import ConfirmationModal from '../components/ConfirmationModal';
import PlatformAccountsPanel from '../components/PlatformAccountsPanel';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useToastMessage } from '../contexts/ToastMessageContext.jsx';
import { FinanceValidation } from '../utils/financeValidation';
import apiClient from '../services/apiClient';

function mapFeeSettingsFromApi(feeSettings = {}, settlementConfig = {}) {
  const shareSum =
    (Number(feeSettings.kairosSharePercent) || 0) + (Number(feeSettings.hectoSharePercent) || 0);
  const rate = Number(
    feeSettings.remittanceFeeRatePercent ?? (shareSum > 0 ? shareSum : 2.5)
  );
  const kairos = Number(
    settlementConfig.kairosSharePercent ?? feeSettings.kairosSharePercent ?? 1.5
  );
  const hecto = Number(
    settlementConfig.hectoSharePercent ?? feeSettings.hectoSharePercent ?? 1.0
  );
  const hectoPerTx = Number(
    settlementConfig.hectoFeePerTransactionKRW ?? feeSettings.hectoFeePerTransactionKRW ?? 3000
  );
  return {
    ...feeSettings,
    remittanceFeeRatePercent: Number.isFinite(rate) ? rate : 2.5,
    kairosSharePercent: Number.isFinite(kairos) ? kairos : 1.5,
    hectoSharePercent: Number.isFinite(hecto) ? hecto : 1.0,
    hectoFeePerTransactionKRW: Number.isFinite(hectoPerTx) && hectoPerTx >= 0 ? hectoPerTx : 3000,
  };
}

function toFeeInputState(feeSettings = {}) {
  return {
    remittanceFeeRatePercent: String(feeSettings.remittanceFeeRatePercent ?? 2.5),
    kairosSharePercent: String(feeSettings.kairosSharePercent ?? 1.5),
    hectoSharePercent: String(feeSettings.hectoSharePercent ?? 1.0),
    hectoFeePerTransactionKRW: String(feeSettings.hectoFeePerTransactionKRW ?? 3000),
  };
}

function parseFeeInputs(inputFeeSettings = {}, fallback = {}) {
  const rate = parseFloat(inputFeeSettings.remittanceFeeRatePercent);
  const kairos = parseFloat(inputFeeSettings.kairosSharePercent);
  const hecto = parseFloat(inputFeeSettings.hectoSharePercent);
  const hectoPerTx = parseFloat(inputFeeSettings.hectoFeePerTransactionKRW);
  return {
    remittanceFeeRatePercent: Number.isFinite(rate) ? rate : (fallback.remittanceFeeRatePercent ?? 2.5),
    kairosSharePercent: Number.isFinite(kairos) ? kairos : (fallback.kairosSharePercent ?? 1.5),
    hectoSharePercent: Number.isFinite(hecto) ? hecto : (fallback.hectoSharePercent ?? 1.0),
    hectoFeePerTransactionKRW:
      Number.isFinite(hectoPerTx) && hectoPerTx >= 0
        ? Math.round(hectoPerTx)
        : (fallback.hectoFeePerTransactionKRW ?? 3000),
  };
}

const FinanceSettingsPage = () => {
  // // const { t } = useTranslation();  // Admin은 다국어 지원 불필요// Admin은 다국어 지원 불필요
  const { showToastMessage } = useToastMessage();
  
  // Exchange rates state (데이터베이스에서 로드)
  const [exchangeRates, setExchangeRates] = useState({
    usdToKrw: 0, // Admin에서 반드시 설정해야 함
    usdToMmk: 0, // Admin에서 반드시 설정해야 함
    krwToMmk: 0  // 자동 계산
  });
  
  // Input states for manual rate entry
  const [inputUsdToKrw, setInputUsdToKrw] = useState('');
  const [inputUsdToMmk, setInputUsdToMmk] = useState('');
  const [inputKrwToMmk, setInputKrwToMmk] = useState('');
  
  // Fee settings state (데이터베이스에서 로드) — 요율(%) 기준
  const [feeSettings, setFeeSettings] = useState({
    remittanceFeeRatePercent: 2.5,
    kairosSharePercent: 1.5,
    hectoSharePercent: 1.0,
    hectoFeePerTransactionKRW: 3000,
  });
  
  // Input states for manual fee entry
  const [inputFeeSettings, setInputFeeSettings] = useState({
    remittanceFeeRatePercent: '2.5',
    kairosSharePercent: '1.5',
    hectoSharePercent: '1.0',
    hectoFeePerTransactionKRW: '3000',
  });
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Confirmation states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning'
  });
  
  // Exchange rate history for chart
  const [exchangeRateHistory, setExchangeRateHistory] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  
  // Real data stats
  const [realStats, setRealStats] = useState({
    today: { amount: 0, count: 0, feeRevenue: 0 },
    month: { amount: 0, count: 0, feeRevenue: 0 },
    feeSettings: { remittanceFeeRatePercent: 2.5, kairosSharePercent: 1.5, hectoSharePercent: 1.0 }
  });

  const [financeTab, setFinanceTab] = useState('rates'); // rates | accounts
  

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
    loadExchangeRateHistory();
    loadFinanceStats();
  }, []);

  // KRW-MMK 자동계산 (USD 기준) — USD/MMK 수동값 기준
  useEffect(() => {
    if (exchangeRates.usdToKrw && exchangeRates.usdToMmk) {
      const calculatedKrwToMmk = exchangeRates.usdToMmk / exchangeRates.usdToKrw;
      setExchangeRates((prev) => ({
        ...prev,
        krwToMmk: parseFloat(calculatedKrwToMmk.toFixed(4)),
      }));
    }
  }, [exchangeRates.usdToKrw, exchangeRates.usdToMmk]);

  // USD/KRW 자동 동기화 시 차트만 갱신 (USD/MMK는 수동 고정)
  useEffect(() => {
    if (exchangeRates.usdToKrw) {
      loadExchangeRateHistory();
    }
  }, [exchangeRates.usdToKrw]);

  // Load settings from backend API
  const loadSettings = async () => {
    try {
      setLoading(true);
      console.log('🔧 금융설정 로드 시작...');
      
      // 백엔드 API에서 금융설정 로드
      try {
        const response = await apiClient.get('/api/admin/finance/settings');
        console.log('✅ 백엔드 API 응답:', response);
        
        if (response.success && response.data) {
          const data = response.data;
          
          // 환율 설정 업데이트
          if (data.exchangeRates) {
            setExchangeRates(data.exchangeRates);
            setInputUsdToKrw(data.exchangeRates.usdToKrw?.toString() || '');
            setInputUsdToMmk(data.exchangeRates.usdToMmk?.toString() || '');
            setInputKrwToMmk(data.exchangeRates.krwToMmk?.toString() || '');
          }
          
          // 수수료·정산 요율 설정 업데이트
          if (data.feeSettings || data.settlementConfig) {
            const mapped = mapFeeSettingsFromApi(data.feeSettings, data.settlementConfig);
            setFeeSettings(mapped);
            setInputFeeSettings(toFeeInputState(mapped));
          }
          
          console.log('✅ 백엔드 API 데이터로 업데이트 완료');
        } else {
          throw new Error('API 응답이 올바르지 않습니다.');
        }
      } catch (apiError) {
        console.warn('⚠️ 백엔드 API 로드 실패, Firestore 폴백 시도:', apiError);
        
        // API 실패 시 Firestore 폴백
        try {
          const financeConfigRef = doc(db, `artifacts/${appId}/settings`, 'finance');
          const financeConfigSnap = await getDoc(financeConfigRef);
          
          if (financeConfigSnap.exists()) {
            const data = financeConfigSnap.data();
            console.log('✅ Firestore 폴백 데이터 로드 완료:', data);
            
            // 환율 설정 업데이트
            if (data.exchangeRates) {
              setExchangeRates(data.exchangeRates);
              setInputUsdToKrw(data.exchangeRates.usdToKrw?.toString() || '');
              setInputUsdToMmk(data.exchangeRates.usdToMmk?.toString() || '');
              setInputKrwToMmk(data.exchangeRates.krwToMmk?.toString() || '');
            }
            
            if (data.feeSettings || data.settlementConfig) {
              const mapped = mapFeeSettingsFromApi(data.feeSettings, data.settlementConfig);
              setFeeSettings(mapped);
              setInputFeeSettings(toFeeInputState(mapped));
            }
          } else {
            throw new Error('Firestore에도 데이터가 없습니다.');
          }
        } catch (firestoreError) {
          console.warn('⚠️ Firestore 폴백도 실패, 기본값 사용:', firestoreError);
          
          // 기본값으로 초기화 (환율 설정 필수)
          const defaultExchangeRates = {
            usdToKrw: null,  // Admin에서 반드시 설정해야 함
            usdToMmk: null,  // Admin에서 반드시 설정해야 함
            krwToMmk: null   // 자동 계산
          };
          
          const defaultFeeSettings = {
            remittanceFeeRatePercent: 2.5,
            kairosSharePercent: 1.5,
            hectoSharePercent: 1.0,
            hectoFeePerTransactionKRW: 3000,
          };
          
          setExchangeRates(defaultExchangeRates);
          setFeeSettings(defaultFeeSettings);
          setInputUsdToKrw('');
          setInputUsdToMmk('');
          setInputKrwToMmk('');
          setInputFeeSettings(toFeeInputState(defaultFeeSettings));
          
          console.log('✅ 기본값으로 초기화 완료');
        }
      }
      
    } catch (error) {
      console.error('❌ 금융설정 로드 중 예상치 못한 오류:', error);
      showToastMessage('금융설정을 불러오는 중 오류가 발생했습니다. 기본값을 사용합니다.', 'warning');
    } finally {
      setLoading(false);
    }
  };

  // Load exchange rate history for chart
  const loadExchangeRateHistory = async () => {
    try {
      setChartLoading(true);
      console.log('📊 환율 변동 데이터 로드 시작...');
      
      // 백엔드 API에서 환율 히스토리 로드
      try {
        const response = await apiClient.get('/api/admin/finance/exchange-rate-history?days=30');
        console.log('✅ 백엔드 API 환율 히스토리 응답:', response);
        
        if (response.success && response.data && response.data.length > 0) {
          const historyData = response.data.map(item => ({
            date: item.date.toISOString().split('T')[0],
            displayDate: new Date(item.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }),
            usdToKrw: item.usdToKrw,
            usdToMmk: item.usdToMmk,
            krwToMmk: item.krwToMmk
          }));
          
          console.log('✅ 백엔드 API 환율 히스토리 데이터 로드 완료:', historyData);
          setExchangeRateHistory(historyData);
        } else {
          throw new Error('API 응답이 올바르지 않습니다.');
        }
      } catch (apiError) {
        console.warn('⚠️ 백엔드 API 환율 히스토리 로드 실패, 시뮬레이션 데이터 생성:', apiError);
        
        // API 실패 시 시뮬레이션 데이터 생성
        const today = new Date();
        const historyData = [];
        
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          
          // 현재 환율 기준으로 변동 시뮬레이션 (USD/KRW만 — USD/MMK는 수동 고정)
          const baseUsdToKrw = exchangeRates.usdToKrw || 1350;
          const baseUsdToMmk = exchangeRates.usdToMmk || 2100;

          const variation = (Math.random() - 0.5) * 0.04; // ±2%
          const usdToKrw = Math.round(baseUsdToKrw * (1 + variation));
          const usdToMmk = baseUsdToMmk;
          const krwToMmk = parseFloat((usdToMmk / usdToKrw).toFixed(4));
          
          historyData.push({
            date: date.toISOString().split('T')[0],
            displayDate: date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }),
            usdToKrw,
            usdToMmk,
            krwToMmk
          });
        }
        
        console.log('📊 시뮬레이션 환율 변동 데이터 생성 완료:', historyData);
        setExchangeRateHistory(historyData);
      }
    } catch (error) {
      console.error('❌ 환율 변동 데이터 로드 실패:', error);
    } finally {
      setChartLoading(false);
    }
  };

  // Load finance stats from backend API
  const loadFinanceStats = async () => {
    try {
      console.log('📊 금융 통계 로드 시작...');
      
      const response = await apiClient.get('/api/admin/finance/stats');
      console.log('✅ 백엔드 API 금융 통계 응답:', response);
      
      if (response.success && response.data) {
        setRealStats(response.data);
        console.log('✅ 실 데이터 통계 로드 완료:', response.data);
      } else {
        throw new Error('API 응답이 올바르지 않습니다.');
      }
    } catch (error) {
      console.warn('⚠️ 백엔드 API 금융 통계 로드 실패, 기본값 사용:', error);
      // 기본값은 이미 초기화되어 있음
    }
  };

  // Save settings to Firebase with error handling
  const handleSaveClick = () => {
    try {
      setConfirmModal({
        isOpen: true,
        title: '첫 번째 확인',
        message: '금융설정을 저장하시겠습니까?\n이 설정은 송금업체와의 수수료 정산에 영향을 미칩니다.',
        type: 'warning'
      });
    } catch (error) {
      console.error('❌ 저장 확인 모달 오류:', error);
      showToastMessage('저장 확인 중 오류가 발생했습니다', 'error');
    }
  };

  const handleFirstConfirm = () => {
    setConfirmModal({
      isOpen: true,
      title: '두 번째 확인',
      message: '정말로 저장하시겠습니까?\n이 작업은 되돌릴 수 없습니다.',
      type: 'danger'
    });
  };

  const handleConfirmModalClose = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmModalConfirm = async () => {
    if (confirmModal.title === '첫 번째 확인') {
      handleFirstConfirm();
    } else {
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      await saveSettings();
    }
  };

  const saveSettings = async () => {
    try {
      console.log('💾 금융설정 저장 시작...');
      setSaving(true);
      
      // 입력값 검증 및 기본값 설정
      const safeInputUsdToKrw = inputUsdToKrw && !isNaN(parseFloat(inputUsdToKrw)) ? parseFloat(inputUsdToKrw) : (exchangeRates.usdToKrw || 0);
      const safeInputUsdToMmk = inputUsdToMmk && !isNaN(parseFloat(inputUsdToMmk)) ? parseFloat(inputUsdToMmk) : (exchangeRates.usdToMmk || 0);
      const safeInputKrwToMmk = inputKrwToMmk && !isNaN(parseFloat(inputKrwToMmk)) ? parseFloat(inputKrwToMmk) : (exchangeRates.krwToMmk || 0);
      
      // 저장 전 금융설정 검증
      const financeConfig = {
        exchangeRates: {
          usdToKrw: safeInputUsdToKrw,
          usdToMmk: safeInputUsdToMmk,
          krwToMmk: safeInputKrwToMmk
        },
        feeSettings: parseFeeInputs(inputFeeSettings, feeSettings),
        settlementConfig: {
          kairosSharePercent: parseFeeInputs(inputFeeSettings, feeSettings).kairosSharePercent,
          hectoSharePercent: parseFeeInputs(inputFeeSettings, feeSettings).hectoSharePercent,
          hectoFeePerTransactionKRW: parseFeeInputs(inputFeeSettings, feeSettings).hectoFeePerTransactionKRW,
        },
      };
      
      console.log('🔍 금융설정 검증 중...', financeConfig);
      console.log('🔍 입력값 상세:', {
        inputUsdToKrw,
        inputUsdToMmk,
        inputKrwToMmk,
        inputFeeSettings
      });
      console.log('🔍 안전값 상세:', {
        safeInputUsdToKrw,
        safeInputUsdToMmk,
        safeInputKrwToMmk
      });
      
      const validation = await FinanceValidation.validateBeforeSave(financeConfig);
      
      console.log('🔍 검증 결과:', validation);
      console.log('🔍 검증 성공 여부:', validation.success);
      console.log('🔍 검증 메시지:', validation.message);
      console.log('🔍 검증 오류들:', validation.errors);
      console.log('🔍 환율 검증:', validation.exchangeRates);
      console.log('🔍 수수료 검증:', validation.feeSettings);
      
      if (!validation.success) {
        console.error('❌ Finance validation failed - 상세 정보:');
        console.error('❌ Message:', validation.message);
        console.error('❌ Errors:', validation.errors);
        console.error('❌ Errors (배열 내용):', JSON.stringify(validation.errors, null, 2));
        console.error('❌ Exchange Rates Validation:', validation.exchangeRates);
        console.error('❌ Exchange Rates Validation (상세):', JSON.stringify(validation.exchangeRates, null, 2));
        console.error('❌ Fee Settings Validation:', validation.feeSettings);
        console.error('❌ Fee Settings Validation (상세):', JSON.stringify(validation.feeSettings, null, 2));
        
        const errorMessage = validation.errors && validation.errors.length > 0 
          ? validation.errors.join(', ') 
          : validation.message || '알 수 없는 검증 오류';
        
        showToastMessage(`검증 실패: ${errorMessage}`, 'error');
        return;
      }
      
      // 경고가 있는 경우 사용자에게 알림
      if (validation.warnings && validation.warnings.length > 0) {
        console.warn('⚠️ Finance validation warnings:', validation.warnings);
        showToastMessage(`저장 완료 (경고: ${validation.warnings.length}개)`, 'warning');
      }
      
      const newExchangeRates = {
        usdToKrw: safeInputUsdToKrw,
        usdToMmk: safeInputUsdToMmk,
        krwToMmk: safeInputKrwToMmk
      };
      
      const newFeeSettings = financeConfig.feeSettings;
      const newSettlementConfig = financeConfig.settlementConfig;
      
      const settingsData = {
        exchangeRates: newExchangeRates,
        feeSettings: newFeeSettings,
        settlementConfig: newSettlementConfig,
        lastUpdated: new Date(),
        updatedBy: 'admin',
        // 송금 시스템 연동용 데이터도 함께 저장
        remittanceData: validation.remittanceData
      };
      
      console.log('💾 백엔드 API에 저장 중...', settingsData);
      
      // 백엔드 API에 저장
      try {
        const response = await apiClient.put('/api/admin/finance/settings', {
          exchangeRates: newExchangeRates,
          feeSettings: newFeeSettings,
          settlementConfig: newSettlementConfig,
        });
        
        console.log('✅ 백엔드 API 저장 완료:', response);
        
        if (response.success) {
          const savedRates = response.data?.exchangeRates || newExchangeRates;
          const savedFees = response.data?.feeSettings || newFeeSettings;
          setExchangeRates(savedRates);
          setFeeSettings(savedFees);
          setInputUsdToKrw(savedRates.usdToKrw?.toString() || '');
          setInputUsdToMmk(savedRates.usdToMmk?.toString() || '');
          setInputKrwToMmk(savedRates.krwToMmk?.toString() || '');
          setInputFeeSettings(toFeeInputState(mapFeeSettingsFromApi(savedFees, response.data?.settlementConfig)));
          showToastMessage('금융설정이 성공적으로 저장되었습니다', 'success');
          
          // 환율 히스토리 저장
          try {
            await apiClient.post('/api/admin/finance/exchange-rate-history', {
              usdToKrw: newExchangeRates.usdToKrw,
              usdToMmk: newExchangeRates.usdToMmk
            });
            console.log('✅ 환율 히스토리 저장 완료');
          } catch (historyError) {
            console.warn('⚠️ 환율 히스토리 저장 실패:', historyError);
          }
        } else {
          throw new Error(response.message || '저장에 실패했습니다.');
        }
      } catch (apiError) {
        console.warn('⚠️ 백엔드 API 저장 실패, Firestore 폴백 시도:', apiError);
        
        // API 실패 시 Firestore 폴백
        const fallbackData = {
          ...settingsData,
          manualUsdToMmk: safeInputUsdToMmk,
          exchangeRatesUsdMmkSource: 'manual',
          exchangeRatesManualUpdatedAt: new Date(),
        };
        const financeConfigRef = doc(db, `artifacts/${appId}/settings`, 'finance');
        await setDoc(financeConfigRef, fallbackData, { merge: true });
        try {
          await setDoc(doc(db, 'finance_settings', 'main'), fallbackData, { merge: true });
        } catch (mainDocError) {
          console.warn('⚠️ finance_settings/main 폴백 저장 실패:', mainDocError);
        }
        
        console.log('✅ Firestore 폴백 저장 완료');
        setExchangeRates(newExchangeRates);
        setFeeSettings(newFeeSettings);
        setInputUsdToKrw(newExchangeRates.usdToKrw?.toString() || '');
        setInputUsdToMmk(newExchangeRates.usdToMmk?.toString() || '');
        setInputKrwToMmk(newExchangeRates.krwToMmk?.toString() || '');
        setInputFeeSettings(toFeeInputState(newFeeSettings));
        showToastMessage(
          '금융설정이 Firestore에만 저장되었습니다. USD/MMK가 다시 바뀌면 API 저장 경로를 확인해 주세요.',
          'warning'
        );
      }
    } catch (error) {
      console.error('❌ 금융설정 저장 실패:', error);
      showToastMessage(`설정 저장에 실패했습니다: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Update fee input setting
  const updateFeeInputSetting = (key, value) => {
    setInputFeeSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Real data stats for enhanced dashboard
  const stats = {
    totalTransactions: realStats.month.count,
    totalVolume: realStats.month.amount,
    averageFee: realStats.feeSettings.remittanceFeeRatePercent ?? 2.5,
    successRate: 98.7, // This would need to be calculated from real data
    lastUpdated: new Date().toLocaleString('ko-KR')
  };

  const mockPerformanceData = [
    { name: 'USD/KRW', value: exchangeRates.usdToKrw || 0, change: 2.3, trend: 'up' },
    { name: 'USD/MMK', value: exchangeRates.usdToMmk || 0, change: -1.2, trend: 'down' },
    { name: 'KRW/MMK', value: exchangeRates.krwToMmk || 0, change: 0.8, trend: 'up' }
  ];

  const pieData = [
    { name: 'USD/KRW', value: 45, color: '#3b82f6' },
    { name: 'USD/MMK', value: 35, color: '#10b981' },
    { name: 'KRW/MMK', value: 20, color: '#8b5cf6' }
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 text-gray-800 p-6 rounded-b-2xl shadow-sm border-b border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-1 flex items-center">
                <Sparkles className="w-6 h-6 mr-2 text-blue-400" />
                Finance Dashboard
              </h1>
              <p className="text-gray-600 text-sm">Advanced financial management & real-time monitoring</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3 border border-gray-200">
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4 text-green-500" />
                  <span className="text-xs font-medium text-gray-700">Live Data</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">Last updated: {stats.lastUpdated}</div>
              </div>
              <button
                onClick={loadSettings}
                disabled={loading}
                className="bg-white/60 backdrop-blur-sm hover:bg-white/80 rounded-lg p-2 transition-all duration-200 border border-gray-200"
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </motion.div>

        <div className="p-4 space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFinanceTab('rates')}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                financeTab === 'rates'
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
            >
              환율 · 송금수수료
            </button>
            <button
              type="button"
              onClick={() => setFinanceTab('accounts')}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                financeTab === 'accounts'
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-white text-slate-600 border border-slate-200'
              }`}
            >
              계좌관리
            </button>
          </div>

          {financeTab === 'accounts' ? (
            <PlatformAccountsPanel />
          ) : (
          <>
          {/* 환율 및 수수료 설정 필수 경고 */}
          {(!exchangeRates.usdToKrw || !exchangeRates.usdToMmk || (feeSettings.remittanceFeeRatePercent === undefined || feeSettings.remittanceFeeRatePercent === null)) && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-4"
            >
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-red-800">환율 및 수수료 설정이 필요합니다</h3>
                  <p className="text-red-700 text-sm mt-1">
                    송금 서비스를 사용하려면 반드시 환율과 수수료를 설정해야 합니다. 
                    아래에서 USD/KRW, USD/MMK 환율과 송금 수수료를 입력해주세요.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Key Metrics Cards - Compact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3"
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center text-green-600">
                  <TrendingUpIcon className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">+2.3%</span>
                </div>
              </div>
              <h3 className="text-xs font-medium text-gray-600 mb-1">Total Volume</h3>
              <p className="text-lg font-bold text-gray-900">${(stats.totalVolume / 1000000).toFixed(1)}M</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Target className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">98.7%</span>
                </div>
              </div>
              <h3 className="text-xs font-medium text-gray-600 mb-1">Success Rate</h3>
              <p className="text-lg font-bold text-gray-900">{stats.successRate}%</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calculator className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex items-center text-blue-600">
                  <Zap className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">Active</span>
                </div>
              </div>
              <h3 className="text-xs font-medium text-gray-600 mb-1">Avg Fee</h3>
              <p className="text-lg font-bold text-gray-900">₩{stats.averageFee.toLocaleString()}</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-lg p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Activity className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex items-center text-orange-600">
                  <Clock className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">Live</span>
                </div>
              </div>
              <h3 className="text-xs font-medium text-gray-600 mb-1">Transactions</h3>
              <p className="text-lg font-bold text-gray-900">{stats.totalTransactions.toLocaleString()}</p>
            </motion.div>
          </motion.div>

          {/* Exchange Rates Overview - Compact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-700 flex items-center">
                <Globe className="w-5 h-5 mr-2 text-blue-500" />
                Live Exchange Rates
              </h2>
            </div>
            
            <div className="p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {mockPerformanceData.map((rate, index) => (
                  <motion.div
                    key={rate.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className={`p-1.5 rounded-md ${
                          rate.name === 'USD/KRW' ? 'bg-blue-100' :
                          rate.name === 'USD/MMK' ? 'bg-green-100' : 'bg-purple-100'
                        }`}>
                          <DollarSign className={`w-4 h-4 ${
                            rate.name === 'USD/KRW' ? 'text-blue-600' :
                            rate.name === 'USD/MMK' ? 'text-green-600' : 'text-purple-600'
                          }`} />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{rate.name}</h3>
                        </div>
                      </div>
                      <div className={`flex items-center space-x-1 ${
                        rate.trend === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {rate.trend === 'up' ? (
                          <ArrowUpRight className="w-3 h-3" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3" />
                        )}
                        <span className="text-xs font-medium">{rate.change}%</span>
                      </div>
                    </div>
                    
                    <div className="text-xl font-bold text-gray-900 mb-1">
                      {rate.name === 'KRW/MMK' ? 
                        (rate.value ? rate.value.toFixed(4) : '0.0000') : 
                        (rate.value ? rate.value.toLocaleString() : '0')
                      }
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>
                        {rate.name === 'USD/KRW' ? 'Korean Won' :
                         rate.name === 'USD/MMK' ? 'Myanmar Kyat' : 'Myanmar Kyat'}
                      </span>
                      <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                        {rate.trend === 'up' ? 'Rising' : 'Falling'}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Settings Panels - Compact */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Exchange Rates Configuration */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-700 flex items-center">
                  <Settings className="w-4 h-4 mr-2 text-green-500" />
                  Exchange Rate Configuration
                </h2>
              </div>
              
              <div className="p-3 space-y-3">
                {/* USD to KRW */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-gray-700">
                    USD to KRW
                    <span className="ml-1 text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">자동</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={inputUsdToKrw}
                      onChange={e => {
                        setInputUsdToKrw(e.target.value);
                        if (e.target.value && inputUsdToMmk) {
                          const calculatedKrwToMmk = parseFloat(inputUsdToMmk) / parseFloat(e.target.value);
                          setInputKrwToMmk(calculatedKrwToMmk.toFixed(4));
                        }
                      }}
                      placeholder="Enter USD to KRW rate"
                      className="w-full p-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-sm"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">KRW</div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Current: {(exchangeRates.usdToKrw || 0).toLocaleString()}</span>
                    <span className="text-blue-600 font-medium">헥토/외부 API 동기화</span>
                  </div>
                </div>

                {/* USD to MMK — 수동 전용 */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-gray-700">
                    USD to MMK
                    <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">수동</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={inputUsdToMmk}
                      onChange={e => {
                        setInputUsdToMmk(e.target.value);
                        if (e.target.value && inputUsdToKrw) {
                          const calculatedKrwToMmk = parseFloat(e.target.value) / parseFloat(inputUsdToKrw);
                          setInputKrwToMmk(calculatedKrwToMmk.toFixed(4));
                        }
                      }}
                      placeholder="Enter USD to MMK rate"
                      className="w-full p-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-green-500 focus:border-green-500 transition-all duration-200 text-sm"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">MMK</div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Current: {(exchangeRates.usdToMmk || 0).toLocaleString()}</span>
                    <span className="text-amber-700 font-medium">자동 변경 없음</span>
                  </div>
                  <p className="text-[10px] text-amber-800/80 leading-relaxed">
                    USD→MMK는 미얀마 현지 수취 표시용 수동 환율입니다. USD→KRW 자동 동기화가 이 값을 바꾸지 않습니다.
                  </p>
                </div>

                {/* KRW to MMK (Auto-calculated) */}
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-gray-700">
                    KRW to MMK 
                    <span className="ml-1 text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">Auto</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={inputKrwToMmk || (exchangeRates.krwToMmk || 0)}
                      readOnly
                      className="w-full p-2 border border-gray-200 rounded-lg bg-blue-50 text-gray-700 cursor-not-allowed text-sm"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">MMK</div>
                  </div>
                  <div className="text-xs text-blue-600">
                    Formula: USD/MMK ÷ USD/KRW = {(exchangeRates.krwToMmk || 0).toFixed(4)}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Fee Settings Configuration */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-700 flex items-center">
                  <Calculator className="w-4 h-4 mr-2 text-purple-500" />
                  Fee Configuration
                </h2>
              </div>
              
              <div className="p-3 space-y-3">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-gray-700">
                    총 수수료 요율 (송금+환전)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="20"
                      value={inputFeeSettings.remittanceFeeRatePercent}
                      onChange={(e) => updateFeeInputSetting('remittanceFeeRatePercent', e.target.value)}
                      placeholder="예: 2.5"
                      className="w-full p-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-sm"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">%</div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      현재: {feeSettings.remittanceFeeRatePercent ?? 2.5}%
                    </span>
                    <span className="text-purple-600 font-medium">송금액 기준</span>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="flex items-start space-x-1">
                      <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-700">
                        예: 100,000원 송금 시 수수료 {(100000 * ((parseFloat(inputFeeSettings.remittanceFeeRatePercent) || feeSettings.remittanceFeeRatePercent || 2.5) / 100)).toLocaleString()}원
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-gray-700">
                    헥토파이낸스 건당 정산액
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="100"
                      min="0"
                      max="100000"
                      value={inputFeeSettings.hectoFeePerTransactionKRW}
                      onChange={(e) => updateFeeInputSetting('hectoFeePerTransactionKRW', e.target.value)}
                      placeholder="예: 3000"
                      className="w-full p-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-sm"
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">원 / 건</div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      현재: {Number(feeSettings.hectoFeePerTransactionKRW ?? 3000).toLocaleString()}원 / 건
                    </span>
                    <span className="text-purple-600 font-medium">Kairos = 수수료 − 헥토</span>
                  </div>
                </div>

                {(() => {
                  const ratePct = parseFloat(inputFeeSettings.remittanceFeeRatePercent) || feeSettings.remittanceFeeRatePercent || 2.5;
                  const hectoInput = parseFloat(inputFeeSettings.hectoFeePerTransactionKRW);
                  const hectoPerTx = Number.isFinite(hectoInput) && hectoInput >= 0 ? Math.round(hectoInput) : (feeSettings.hectoFeePerTransactionKRW ?? 3000);
                  const breakEven = ratePct > 0 ? Math.ceil(hectoPerTx / (ratePct / 100)) : null;
                  return (
                    <div className="bg-gray-50 rounded-lg p-2">
                      <h3 className="text-xs font-semibold text-gray-700 mb-1.5">정산 예시 (건당)</h3>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-gray-500">
                            <th className="py-0.5 text-left font-medium">송금액</th>
                            <th className="py-0.5 text-right font-medium">수수료 {ratePct}%</th>
                            <th className="py-0.5 text-right font-medium">헥토</th>
                            <th className="py-0.5 text-right font-medium">Kairos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[100000, 300000, 500000, 1000000].map((amt) => {
                            const fee = Math.round(amt * (ratePct / 100));
                            const kairos = fee - hectoPerTx;
                            return (
                              <tr key={amt} className="border-t border-gray-200">
                                <td className="py-0.5 text-gray-700">{amt.toLocaleString()}원</td>
                                <td className="py-0.5 text-right font-semibold text-gray-900">{fee.toLocaleString()}원</td>
                                <td className="py-0.5 text-right font-semibold text-green-700">{hectoPerTx.toLocaleString()}원</td>
                                <td className={`py-0.5 text-right font-semibold ${kairos < 0 ? 'text-red-600' : 'text-blue-700'}`}>
                                  {kairos.toLocaleString()}원
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {breakEven != null && (
                        <p className="mt-1.5 text-[11px] text-amber-700">
                          송금액 {breakEven.toLocaleString()}원 미만이면 수수료가 헥토 정산액보다 작아 Kairos 몫이 마이너스가 됩니다.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          </div>


          {/* Action Buttons - Compact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex justify-center space-x-3"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSaveClick}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span className="font-semibold text-sm">
                {saving ? 'Saving...' : 'Save Settings'}
              </span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={loadSettings}
              disabled={loading}
              className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="font-semibold text-sm">Refresh</span>
            </motion.button>
          </motion.div>

          {/* Analytics Dashboard - Compact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-700 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-indigo-500" />
                Financial Analytics
              </h2>
            </div>
            
            <div className="p-3">
              {chartLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-indigo-600" />
                    <span className="text-gray-600 text-sm">Loading analytics...</span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Exchange Rate Trends */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                      <TrendingUpIcon className="w-4 h-4 mr-1 text-blue-600" />
                      Exchange Rate Trends (7 Days)
                    </h3>
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={exchangeRateHistory}>
                          <defs>
                            <linearGradient id="usdKrwGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="usdMmkGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="2 2" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="displayDate" 
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickLine={{ stroke: '#e5e7eb' }}
                          />
                          <YAxis 
                            tick={{ fontSize: 10, fill: '#6b7280' }}
                            tickLine={{ stroke: '#e5e7eb' }}
                            tickFormatter={(value) => `${(value/1000).toFixed(0)}K`}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '12px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                            formatter={(value, name) => [
                              `${value.toLocaleString()} ${name === 'usdToKrw' ? 'KRW' : 'MMK'}`,
                              name === 'usdToKrw' ? 'USD/KRW' : 'USD/MMK'
                            ]}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="usdToKrw" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            fill="url(#usdKrwGradient)"
                            dot={{ fill: '#3b82f6', strokeWidth: 1, r: 2 }}
                            activeDot={{ r: 4, stroke: '#3b82f6', strokeWidth: 2 }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="usdToMmk" 
                            stroke="#10b981" 
                            strokeWidth={2}
                            fill="url(#usdMmkGradient)"
                            dot={{ fill: '#10b981', strokeWidth: 1, r: 2 }}
                            activeDot={{ r: 4, stroke: '#10b981', strokeWidth: 2 }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                      <PieChart className="w-4 h-4 mr-1 text-purple-600" />
                      Currency Distribution
                    </h3>
                    <div className="h-32 flex items-center justify-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={60}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: '#ffffff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '12px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                            formatter={(value) => [`${value}%`, 'Distribution']}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-1">
                      {pieData.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-xs text-gray-600">{item.name}</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-900">{item.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
          </>
          )}
        </div>
      </div>

      {/* 2단계 저장 확인 다이얼로그 */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmModalConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        isLoading={saving}
      />
    </>
  );
};

export default FinanceSettingsPage;






